/**
 * Contrast + accent derivation (theming.md §5, NFR-1a). In production the SERVER derives the accent
 * ramp and validates contrast at settings-save time — the browser only previews. This module
 * replicates that math client-side so the Branding admin surface can show the live pass/warn/fail
 * readout and BLOCK save on fail, exactly as the server would (WCAG AA stays a platform property).
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

function clampChannel(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

/**
 * Parse a 3- or 6-digit hex color ("#abc" / "#aabbcc", `#` optional). Returns null if the string
 * isn't a valid hex color (caller falls through to other formats, then the safe default).
 */
function parseHex(hex: string): RGB | null {
  const trimmed = hex.trim();
  const m6 = /^#?([0-9a-f]{6})$/i.exec(trimmed);
  if (m6) {
    const n = parseInt(m6[1]!, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  const m3 = /^#?([0-9a-f]{3})$/i.exec(trimmed);
  if (m3) {
    const [r, g, b] = m3[1]!.split("");
    return {
      r: parseInt(r! + r, 16),
      g: parseInt(g! + g, 16),
      b: parseInt(b! + b, 16),
    };
  }
  return null;
}

/**
 * Parse `rgb(r, g, b)` / `rgba(r, g, b, a)` — integer 0–255 channels, spaces optional, comma or
 * space separated (matches CSS Color 4 legacy + modern syntax; admins hand-type either). Channels
 * are clamped to 0–255. Alpha (if present) is currently IGNORED for palette/contrast math — we
 * treat the typed color as fully opaque against the paper background rather than trying to
 * composite a translucent bg, which could otherwise silently produce an unreadable page. This is a
 * deliberate, safe simplification (see module doc); it never causes a crash or a bad palette.
 */
function parseRgbFunction(value: string): RGB | null {
  const m = /^rgba?\(\s*([^)]+)\)$/i.exec(value.trim());
  if (!m) return null;
  const parts = m[1]!.split(/[\s,/]+/).filter(Boolean);
  // rgb() = 3 channels; rgba() = 3 channels + alpha (alpha ignored, see doc above).
  if (parts.length !== 3 && parts.length !== 4) return null;
  const [r, g, b] = parts;
  const nums = [r, g, b].map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n))) return null;
  return { r: clampChannel(nums[0]!), g: clampChannel(nums[1]!), b: clampChannel(nums[2]!) };
}

/** Parse a hex or rgb()/rgba() color string. Unparseable input returns null (safe-fallback path). */
export function hexToRgb(hex: string): RGB | null {
  const trimmed = hex.trim();
  return parseHex(trimmed) ?? parseRgbFunction(trimmed);
}

export function rgbToHex({ r, g, b }: RGB): string {
  const h = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function channelLum(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance({ r, g, b }: RGB): number {
  return 0.2126 * channelLum(r) + 0.7152 * channelLum(g) + 0.0722 * channelLum(b);
}

/** WCAG contrast ratio between two colors (1–21). */
export function contrastRatio(a: RGB, b: RGB): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Lighten (amount > 0) or darken (< 0) toward white/black by a fraction. */
export function adjust({ r, g, b }: RGB, amount: number): RGB {
  if (amount >= 0) {
    return { r: r + (255 - r) * amount, g: g + (255 - g) * amount, b: b + (255 - b) * amount };
  }
  const k = 1 + amount;
  return { r: r * k, g: g * k, b: b * k };
}

const WHITE = "#FBFAF7";
const INK = "#1A1A17";
const AA = 4.5;

export type ContrastVerdict =
  | { kind: "pass"; ratio: number; contrastColor: string; adjustedAccent?: undefined }
  | { kind: "warn"; ratio: number; contrastColor: string; adjustedAccent: string }
  | { kind: "fail"; ratio: number; contrastColor?: undefined };

/**
 * Evaluate a candidate accent hex against the theming.md §5 rules:
 *  - pass: white OR ink text already meets AA on the accent.
 *  - warn: auto-darkening the accent up to ~16% brings a text color into AA (allowed to save).
 *  - fail: no darkening gets either text color to AA (Save blocked).
 */
export function evaluateAccent(hex: string): ContrastVerdict {
  const rgb = hexToRgb(hex);
  const white = hexToRgb(WHITE)!;
  const ink = hexToRgb(INK)!;
  if (!rgb) return { kind: "fail", ratio: 0 };

  const best = (c: RGB) => {
    const rw = contrastRatio(c, white);
    const ri = contrastRatio(c, ink);
    return rw >= ri ? { ratio: rw, color: WHITE } : { ratio: ri, color: INK };
  };

  const direct = best(rgb);
  if (direct.ratio >= AA) {
    return { kind: "pass", ratio: round2(direct.ratio), contrastColor: direct.color };
  }

  // Try auto-darkening in steps up to 16%.
  for (const amount of [-0.06, -0.1, -0.16]) {
    const darker = adjust(rgb, amount);
    const b = best(darker);
    if (b.ratio >= AA) {
      return {
        kind: "warn",
        ratio: round2(b.ratio),
        contrastColor: b.color,
        adjustedAccent: rgbToHex(darker),
      };
    }
  }
  return { kind: "fail", ratio: round2(direct.ratio) };
}

/**
 * Minimum contrast for a UI component (WCAG 2.1 SC 1.4.11 "Non-text Contrast") — used here to flag
 * the action BUTTON blending into the page background, not just its own label text (#171a).
 */
const UI_MIN = 3.0;

export type BlendVerdict =
  | { kind: "ok"; ratio: number }
  | { kind: "warn"; ratio: number; message: string };

/**
 * Flag when the accent color is too close to the page background — the button would visually blend
 * into the page even if its LABEL text (evaluateAccent) is technically readable (#171a). Non-blocking
 * warn, same pattern as evaluateAccent's "warn".
 */
export function evaluateAccentAgainstBackground(
  accentHex: string,
  backgroundHex: string,
): BlendVerdict {
  const accent = hexToRgb(accentHex);
  const bg = hexToRgb(backgroundHex) ?? hexToRgb(WHITE)!;
  if (!accent) return { kind: "ok", ratio: 0 };
  const ratio = round2(contrastRatio(accent, bg));
  if (ratio < UI_MIN) {
    return {
      kind: "warn",
      ratio,
      message: "Your button color blends into the background.",
    };
  }
  return { kind: "ok", ratio };
}

/**
 * Flag body/ink text below AA (4.5:1) against the page background (#171b) — independent of the
 * accent-button check above. Non-blocking warn.
 */
export function evaluateBodyTextAgainstBackground(
  inkHex: string,
  backgroundHex: string,
): BlendVerdict {
  const ink = hexToRgb(inkHex);
  const bg = hexToRgb(backgroundHex) ?? hexToRgb(WHITE)!;
  if (!ink) return { kind: "ok", ratio: 0 };
  const ratio = round2(contrastRatio(ink, bg));
  if (ratio < AA) {
    return {
      kind: "warn",
      ratio,
      message: "Body text may be hard to read on this background.",
    };
  }
  return { kind: "ok", ratio };
}

/** Derive the 5-shade accent ramp from a single hex (mirrors theming.md §5, light mode). */
export function deriveAccentRamp(hex: string): {
  base: string;
  hover: string;
  active: string;
  weak: string;
  contrast: string;
} | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const verdict = evaluateAccent(hex);
  // Normalize to hex here (not the raw `hex` input) so an admin-typed rgb()/rgba() value never
  // leaks through unnormalized as `base` — every consumer of the ramp expects a hex string.
  const base = verdict.kind === "warn" ? verdict.adjustedAccent : rgbToHex(rgb);
  const baseRgb = hexToRgb(base)!;
  const contrast = verdict.kind === "fail" ? WHITE : (verdict.contrastColor ?? WHITE);
  return {
    base,
    hover: rgbToHex(adjust(baseRgb, -0.08)),
    active: rgbToHex(adjust(baseRgb, -0.16)),
    weak: rgbToHex(adjust(baseRgb, 0.86)),
    contrast,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ---- Resident-form branded palette (page bg + derived ink/surface) --------
//
// The resident forms render ONE branded look pulled from the town's site (no light/dark toggle —
// see ThemeProvider/TownHeader). `backgroundColor` is the only town-chosen input; ink (body text)
// and surface (card) are DERIVED here so body text always keeps >=4.5:1 and large/UI text >=3:1
// (WCAG 2.1 AA, ADA Title II — non-negotiable). If the bg is missing or unusable we fall back to
// the safe default light paper rather than ever rendering unreadable text.

const SAFE_BG = "#FBFAF7"; // --td-paper default (theming.md light ramp)
const SAFE_SURFACE = "#FFFFFF";
const SAFE_INK = "#1A1A17";
const AA_BODY = 4.5;

export interface ResidentPalette {
  /** Page background — the town's color (or the safe default fallback). */
  bg: string;
  /** Card/surface color — derived from bg (a light lift toward white on dark bg, toward black-ish
   *  on light bg is avoided; we keep surface a small step off bg in the SAME direction as ink is
   *  opposite, so cards read as a distinct layer without hurting contrast). */
  surface: string;
  /** Body text color — derived from bg's luminance so contrast on bg is >=4.5:1. */
  ink: string;
  /** Secondary/tertiary text — a step toward bg from ink, still checked to hold >=4.5:1 on bg. */
  ink2: string;
  ink3: string;
  /** Hairline border — a small step off bg (UI-only, needs >=3:1 large/graphical, not body text). */
  border: string;
  borderStrong: string;
  /** Whether we had to fall back to the safe default (bg missing/unusable). */
  usedFallback: boolean;
}

/**
 * Derive a WCAG-AA-safe resident palette from a single town background hex. Contrast safety wins
 * over exact color match: if the given hex is missing/invalid, or if no ink weight can reach AA
 * body contrast against it, we fall back to the default safe light surface entirely.
 */
export function deriveResidentPalette(backgroundColor: string | undefined | null): ResidentPalette {
  const trimmed = (backgroundColor ?? "").trim();
  const rgb = trimmed ? hexToRgb(trimmed) : null;
  if (!rgb) return fallbackPalette();

  const white = hexToRgb(SAFE_SURFACE)!;
  const ink = hexToRgb(SAFE_INK)!;
  const ratioToWhiteInk = contrastRatio(rgb, ink);
  const ratioToWhiteText = contrastRatio(rgb, white);
  const inkIsDark = ratioToWhiteInk >= ratioToWhiteText;
  const bestInkRgb = inkIsDark ? ink : white;
  const bestRatio = inkIsDark ? ratioToWhiteInk : ratioToWhiteText;

  if (bestRatio < AA_BODY) {
    // Neither black nor white ink reaches AA body contrast on this bg — never render unreadable
    // text. Fall back to the safe default surface/text entirely.
    return fallbackPalette();
  }

  const bg = rgbToHex(rgb);
  // Surface: nudge toward the ink direction slightly so cards read as a distinct layer, but only
  // a small amount — re-verify it still keeps ink readable (it's closer to bg than bg itself is
  // to white/black, so contrast can only ever be >= the bg/ink ratio... but re-check anyway).
  const surfaceRgb = inkIsDark ? adjust(rgb, 0.08) : adjust(rgb, -0.08);
  const surfaceRatio = contrastRatio(surfaceRgb, bestInkRgb);
  const surface = surfaceRatio >= AA_BODY ? rgbToHex(surfaceRgb) : bg;

  // ink2/ink3: step ink toward bg for secondary/tertiary text, but keep >=4.5:1 (AA body) — if a
  // step would drop below AA, just reuse full ink instead of guessing.
  const ink2Rgb = adjust(bestInkRgb, inkIsDark ? 0.24 : -0.24);
  const ink2 = contrastRatio(ink2Rgb, rgb) >= AA_BODY ? rgbToHex(ink2Rgb) : rgbToHex(bestInkRgb);
  const ink3Rgb = adjust(bestInkRgb, inkIsDark ? 0.42 : -0.42);
  const ink3 = contrastRatio(ink3Rgb, rgb) >= AA_BODY ? rgbToHex(ink3Rgb) : ink2;

  const borderRgb = adjust(rgb, inkIsDark ? 0.14 : -0.1);
  const borderStrongRgb = adjust(rgb, inkIsDark ? 0.24 : -0.22);

  return {
    bg,
    surface,
    ink: rgbToHex(bestInkRgb),
    ink2,
    ink3,
    border: rgbToHex(borderRgb),
    borderStrong: rgbToHex(borderStrongRgb),
    usedFallback: false,
  };
}

function fallbackPalette(): ResidentPalette {
  return {
    bg: SAFE_BG,
    surface: SAFE_SURFACE,
    ink: SAFE_INK,
    ink2: "#4A4A42",
    ink3: "#6E6E63",
    border: "#E3E1D8",
    borderStrong: "#C9C7BB",
    usedFallback: true,
  };
}
